<template>
  <div>
    <!-- Download Template Section -->
    <div
      class="download-template-section d-flex justify-content-between align-items-center p-3 mb-4"
    >
      <span
        >Download the bulk transfer template to get started with your local
        domestic payouts</span
      >
      <ArgonButton
        color="white"
        variant="outline"
        size="sm"
        class="download-btn"
        @click="downloadTemplate"
      >
        <i class="fas fa-download me-2"></i> Download Template
      </ArgonButton>
    </div>

    <!-- Initial Upload Area / Drag & Drop -->
    <div
      class="upload-container text-center p-5 mb-4"
      v-if="!isUploading && !fileUploaded"
      @dragover.prevent="dragOver = true"
      @dragleave.prevent="dragOver = false"
      @drop.prevent="handleDrop"
      :class="{ 'drag-over': dragOver }"
      @click="$refs.fileInputRef.click()"
    >
      <div class="file-icons mb-3">
        <div class="icon-container csv">.csv</div>
        <div class="icon-container xls">.xlsx</div>
      </div>
      <h6 class="mb-2">Click to upload or drag and drop file here</h6>
      <p class="text-xs text-secondary mb-4">File supported: .csv, .xlsx</p>
      <input
        ref="fileInputRef"
        type="file"
        class="d-none"
        @change="handleFileSelect"
        accept=".csv,.xls,.xlsx"
      />
    </div>

    <!-- Product Type -->
    <div class="card product-type-card mb-4">
      <div class="card-body">
        <div class="product-type-header">
          <h5 class="product-type-title mb-0">Product Type</h5>
          <span class="product-type-subtitle">Select the transfer option that best fits your needs.</span>
        </div>
        <div class="product-options">
          <button
            type="button"
            class="product-option"
            :class="{ 'product-option--selected': productType === 'prime' }"
            @click="productType = 'prime'"
          >
            <span class="product-radio" :class="{ 'product-radio--checked': productType === 'prime' }">
              <span v-if="productType === 'prime'" class="product-radio__dot"></span>
            </span>
            <span class="product-option__label">Prime</span>
            <span class="product-option__desc">Stable &amp; reliable</span>
            <span class="product-option__spacer"></span>
            <span class="product-option__badge">Recommended</span>
          </button>

          <button
            type="button"
            class="product-option"
            :class="{ 'product-option--selected': productType === 'basic' }"
            @click="productType = 'basic'"
          >
            <span class="product-radio" :class="{ 'product-radio--checked': productType === 'basic' }">
              <span v-if="productType === 'basic'" class="product-radio__dot"></span>
            </span>
            <span class="product-option__label">Basic</span>
            <span class="product-option__desc">Lower cost</span>
          </button>
        </div>
      </div>
    </div>

    <!-- Uploading Progress -->
    <div class="upload-container text-center p-5 mb-4" v-if="isUploading">
      <div class="file-icons mb-3">
        <div class="icon-container csv">.csv</div>
        <div class="icon-container xls">.xlsx</div>
      </div>
      <h6 class="mb-3">Uploading...</h6>
      <div class="progress mb-2 mx-auto" style="max-width: 500px; height: 8px">
        <div
          class="progress-bar bg-gradient-success"
          role="progressbar"
          :style="{ width: uploadProgress + '%' }"
          :aria-valuenow="uploadProgress"
          aria-valuemin="0"
          aria-valuemax="100"
        ></div>
      </div>
      <p class="text-sm text-secondary mb-0">{{ uploadProgress }}% complete</p>
    </div>

    <!-- Upload Success View -->
    <div
      class="upload-container text-center p-5 mb-4"
      v-if="fileUploaded && !isUploading && !isProcessing"
    >
      <div class="file-icons mb-3">
        <div class="icon-container csv">.csv</div>
        <div class="icon-container xls">.xlsx</div>
      </div>
      <h6 class="mb-3 text-success">Upload Successfully</h6>
      <div
        class="file-chip d-inline-flex align-items-center bg-light border rounded px-3 py-1"
      >
        <i class="far fa-file-alt me-2"></i>
        <span class="me-2">{{ uploadedFileName }}</span>
        <button
          type="button"
          class="btn-close btn-sm p-0"
          aria-label="Remove file"
          @click="removeFile"
        ></button>
      </div>
    </div>

    <!-- Tips Section -->
    <div class="tips-section mt-4 p-3">
      <h6 class="mb-2">Tips</h6>
      <ol class="text-sm text-secondary ps-3 mb-0">
        <li>
          Please download the batch file format template and fill it in
          according to the instructions.
        </li>
        <li>
          The number of transactions in each batch of files is limited to 1,000
        </li>
      </ol>
    </div>

    <!-- Continue Button -->
    <div class="d-flex justify-content-end mt-4">
      <ArgonButton
        v-if="fileUploaded"
        color="success"
        size="md"
        :disabled="isUploading"
        @click="onContinue"
      >
        Continue
      </ArgonButton>

      <button
        v-else
        class="d-flex justify-content-center align-items-center px-3 py-2"
        style="
          height: 40px;
          gap: 10px;
          border-radius: 4px;
          border: 1px solid #b8c0c1;
          background-color: #dde0e0;
          box-shadow: inset 0px 2px 1px rgba(255, 255, 255, 0.25);
        "
        disabled
      >
        Continue
      </button>
    </div>
  </div>
</template>

<script setup>
import { ref, defineProps, defineEmits } from "vue";
import ArgonButton from "@/components/ArgonButton.vue";
import * as XLSX from "xlsx";
import Swal from "sweetalert2";
import { uploadBatchFile } from "@/api/transfer"; // Import the API function

// eslint-disable-next-line no-unused-vars
const props = defineProps({
  isProcessing: Boolean,
});

// eslint-disable-next-line no-unused-vars
const emit = defineEmits([
  "file-selected",
  "remove-file",
  "download-template",
  "continue",
  "upload-success",
  "upload-failed",
]); // Added new emits

const fileInputRef = ref(null);
const isUploading = ref(false);
const uploadProgress = ref(0);
const fileUploaded = ref(false);
const uploadedFileName = ref("");
const dragOver = ref(false);
const selectedFile = ref(null); // Added from Transfer.vue
const productType = ref("basic");

const handleFileSelect = (event) => {
  const file = event.target.files[0];
  if (!file) return;
  processSelectedFile(file); // Changed to processSelectedFile
};

const handleDrop = (event) => {
  dragOver.value = false;
  const file = event.dataTransfer.files[0];
  if (!file) return;
  processSelectedFile(file); // Changed to processSelectedFile
};

// Replaced processAndEmitFile with processSelectedFile from Transfer.vue
const processSelectedFile = (file) => {
  const allowedTypes = [
    "application/vnd.ms-excel",
    "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
    "text/csv",
    "application/csv",
    "text/x-csv",
    "application/x-csv",
    "text/comma-separated-values",
    "text/x-comma-separated-values",
    "application/excel",
    "application/xls",
    "application/vnd.ms-excel.sheet.macroEnabled.12",
    "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
    "", // Handle cases where MIME type might be empty but extension is valid
  ];
  // Check both MIME type and file extension for better compatibility
  const fileExtension = file.name.split('.').pop().toLowerCase();
  const validExtensions = ['csv', 'xls', 'xlsx'];
  
  if (!allowedTypes.includes(file.type) && !validExtensions.includes(fileExtension)) {
    Swal.fire(
      "Error",
      "Invalid file type. Please upload a CSV or XLSX file.",
      "error"
    );
    return;
  }

  selectedFile.value = file;
  uploadedFileName.value = file.name;
  fileUploaded.value = false;
  isUploading.value = true;
  uploadProgress.value = 0;

  let progress = 0;
  const interval = setInterval(() => {
    progress += 20;
    uploadProgress.value = Math.min(progress, 100);
    if (progress >= 100) {
      clearInterval(interval);
      setTimeout(() => {
        isUploading.value = false;
        fileUploaded.value = true;
        emit("file-selected", selectedFile.value);
      }, 300);
    }
  }, 100);
};

const removeFile = () => {
  fileUploaded.value = false;
  uploadedFileName.value = "";
  selectedFile.value = null; // Clear selectedFile
  isUploading.value = false;
  uploadProgress.value = 0;
  if (fileInputRef.value) {
    fileInputRef.value.value = "";
  }
  emit("remove-file");
};

// Bank options for the template
const bankOptions = [
  "Bank Permata",
  "Bank Negara Indonesia",
  "Bank Rakyat Indonesia",
  "Bank Mandiri",
  "Bank Central Asia",
  "Bank Jago",
  "PT BANK SYARIAH INDONESIA TBK",
  "PT. BANK TABUNGAN NEGARA (Persero)",
  "PT. BANK CIMB NIAGA Tbk.",
  "PT BANK DANAMON INDONESIA Tbk",
  "PT. BANK MEGA Tbk.",
  "PT. BANK ARTHA GRAHA INTERNASIONAL",
  "PT BANK HSBC INDONESIA",
  "PT. BANK OCBC NISP Tbk.",
  "PT. BANK UOB INDONESIA",
  "PT. BANK MUAMALAT INDONESIA",
  "PT. BUKOPIN",
  "PT. BANK BCA SYARIAH",
  "PT. BANK SYARIAH MEGA INDONESIA",
  "PT. BANK RESONA PERDANIA",
  "BPD SUMSEL DAN BABEL",
  "CITIBANK NA",
  "PT. BANK DBS INDONESIA",
  "PT. BANK GANESHA",
  "PT. ALLO BANK INDONESIA TBK",
  "PT. BANK ICBC INDONESIA",
  "PT. BANK SBI INDONESIA",
  "PT. BANK INA PERDANA",
  "PT. BANK JASA JAKARTA",
  "PT. BANK NATIONALNOBU",
  "PT. BANK MASPION INDONESIA",
  "PT. BANK MAYAPADA",
  "PT BANK SHINHAN INDONESIA",
  "PT. BPD BENGKULU",
  "BPD JAWA TIMUR",
  "BPD KALIMANTAN SELATAN",
  "PT.BPD KALTIM DAN KALTARA",
  "BPD LAMPUNG",
  "PT. BPD MALUKU DAN MALUKU UTARA",
  "BPD NUSA TENGGARA TIMUR",
  "BPD SUMATERA UTARA",
  "PT. BANK SINARMAS",
  "STANDARD CHARTERED BANK",
  "PT BANK SEABANK INDONESIA",
  "PT. BPD DKI JAKARTA",
  "PT. BANK COMMONWEALTH",
  "PT. BANK CAPITAL INDONESIA",
  "BANK MAYBANK INDONESIA",
  "BANK PANIN",
  "BANK ANZ INDONESIA",
  "BANK QNB INDONESIA",
  "PT. BANK WOORI SAUDARA INDONESIA",
  "BANK BTPN",
  "BANK BJB SYARIAH",
  "BANK MNC INTERNASIONAL",
  "BANK SAHABAT SAMPOERNA",
  "BANK MULTIARTA SENTOSA",
  "BANK HARDA INTERNASIONAL",
  "BANK CTBC INDONESIA",
  "BPD JAMBI",
  "BPD ACEH SYARIAH",
  "BPD RIAU KEPRI",
  "BPD KALIMANTAN TENGAH",
  "BPD BALI",
  "BPD PAPUA",
  "PT. BANK DIGITAL BCA",
  "PT. SUPER BANK INDONESIA",
  "PT. BANK NEO COMMERCE",
  "OVO",
  "DANA",
  "GOPAY",
  "SHOPEE",
];

const downloadTemplate = () => {
  try {
    // Logic from Transfer.vue to generate and download the template
    const headers = ["Recipient Name", "Bank", "Account Number", "Amount"];
    
    // Create the main worksheet
    const ws = XLSX.utils.aoa_to_sheet([headers]);
    
    // Create a separate sheet for bank options - each option in its own row for better compatibility
    const bankOptionsData = bankOptions.map(bank => [bank]);
    const listSheet = XLSX.utils.aoa_to_sheet(bankOptionsData);
    
    // Set column widths for better visibility
    ws['!cols'] = [
      { width: 20 }, // Recipient Name
      { width: 25 }, // Bank
      { width: 20 }, // Account Number
      { width: 15 }  // Amount
    ];
    
    // Create the workbook
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Template");
    
    // Add the bank list sheet
    XLSX.utils.book_append_sheet(wb, listSheet, "BankList");
    
    // Create a defined name for the bank list range
    if (!wb.Workbook) wb.Workbook = { Names: [] };
    wb.Workbook.Names = [
      {
        Name: "BankOptions",
        Ref: "BankList!$A$1:$A$" + bankOptions.length
      }
    ];
    
    // Add data validation using the defined name
    if (!ws['!dataValidations']) ws['!dataValidations'] = [];
    ws['!dataValidations'].push({
      sqref: "B2:B1000",
      type: "list",
      formula1: "=BankOptions",  // Use the defined name
      showDropDown: true
    });
    
    // Also add direct cell reference validation as a fallback
    ws['!dataValidations'].push({
      sqref: "B2:B1000",
      type: "list",
      formula1: "=BankList!$A$1:$A$" + bankOptions.length,
      showDropDown: true
    });
    
    // Use bookType 'xlsx' with additional options for maximum compatibility
    XLSX.writeFile(wb, "Bulk_Transfer_Template.xlsx", { 
      bookType: 'xlsx',
      bookSST: false,
      type: 'binary',
      cellDates: true,
      compression: true
    });
    
    console.log("Template downloaded successfully");
  } catch (error) {
    console.error("Error generating template:", error);
    // Show error to user
    Swal.fire("Error", "Failed to generate template. Please try again.", "error");
  }
  // emit('download-template'); // Original emit can be kept if parent still needs to know
};

const onContinue = async () => {
  if (!selectedFile.value) return;
  emit("update:isProcessing", true); // Use the prop for global processing state
  try {
    const formData = new FormData();
    formData.append("batch_file", selectedFile.value);
    formData.append("product_type", productType.value);

    const response = await uploadBatchFile(formData);

    if (response && response.status == "success") {
      emit("upload-success", {
        uuid: response.data.uuid,
        fileName: selectedFile.value.name,
      });
    } else {
      console.error("API response missing UUID:", response);
      emit("upload-failed", "API response did not contain a valid UUID.");
      Swal.fire(
        "Error",
        "File upload succeeded but failed to retrieve batch ID. Please try again.",
        "error"
      );
    }
  } catch (error) {
    console.error("Error uploading file:", error);
    Swal.fire("Error", "Failed to upload file. Please try again.", "error");
    emit("upload-failed", error.message || "Unknown upload error");
  } finally {
    emit("update:isProcessing", false);
  }
};

// Expose methods to parent if needed via defineExpose (optional)
// defineExpose({ removeFile });
</script>

<style scoped>
/* Styles from original Transfer.vue related to Step 1 */
.download-template-section {
  background-color: #f8f9fe;
  border-radius: 0.5rem;
  border: 1px solid #e9ecef;
}
.download-template-section span {
  font-size: 0.875rem;
  color: #344767;
}
.download-btn {
  border-color: #2dce89 !important;
  color: #2dce89 !important;
}
.download-btn:hover {
  background-color: #2dce89 !important;
  color: #fff !important;
}
.upload-container {
  border: 1px dashed #cad1d7;
  border-radius: 0.75rem;
  background-color: #f8f9fe;
  transition:
    background-color 0.2s ease,
    border-color 0.2s ease;
  cursor: pointer;
}
.upload-container.drag-over {
  background-color: #e9ecef;
  border-color: #5e72e4;
}
.file-icons {
  display: flex;
  justify-content: center;
  gap: 10px;
}
.icon-container {
  width: 50px;
  height: 60px;
  border: 1px solid #dee2e6;
  border-radius: 4px;
  display: flex;
  justify-content: center;
  align-items: center;
  font-size: 0.75rem;
  font-weight: bold;
  color: #6c757d;
  background-color: #fff;
  position: relative;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
}
.file-chip {
  font-size: 0.875rem;
  color: #000000;
}
.file-chip .btn-close {
  padding: 0.25rem 0.5rem;
  color: #000000;
  margin-left: 0.5rem;
}
.tips-section {
  background-color: #f8f9fa;
  border-radius: 0.5rem;
  border: 1px solid #e9ecef;
}
.tips-section h6 {
  color: #344767;
}

/* ── Product Type ───────────────────────────────────── */
.product-type-card {
  border: 1px solid #dee2e6;
  border-radius: 0.75rem;
  box-shadow: 0 0.125rem 0.25rem rgba(0, 0, 0, 0.075);
}

.product-type-header {
  display: flex;
  align-items: baseline;
  flex-wrap: wrap;
  gap: 10px;
  margin-bottom: 16px;
}

.product-type-title {
  font-size: 1.1rem;
  font-weight: 600;
  color: #344767;
}

.product-type-subtitle {
  font-size: 0.85rem;
  color: #6c757d;
}

.product-options {
  display: flex;
  border: 1.5px solid #e2e8ee;
  border-radius: 14px;
  overflow: hidden;
}

.product-option {
  flex: 1;
  min-width: 0;
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 16px 18px;
  background: #ffffff;
  border: none;
  cursor: pointer;
  text-align: left;
  transition: background-color 0.15s ease;
}

.product-option:not(:last-child) {
  border-right: 1.5px solid #e2e8ee;
}

.product-option:hover {
  background-color: #f8fafb;
}

.product-option--selected {
  background-color: #f0f6ff;
}

.product-option__spacer {
  flex: 1;
}

.product-option__badge {
  display: inline-flex;
  align-items: center;
  padding: 4px 14px;
  background: linear-gradient(90deg, #7de3d8 0%, #a7c9f7 100%);
  border: 1px solid rgba(94, 114, 228, 0.35);
  border-radius: 100px;
  font-size: 12px;
  font-weight: 700;
  color: #14418f;
  white-space: nowrap;
}

.product-radio {
  width: 20px;
  height: 20px;
  border: 2px solid #c5d0d8;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  transition: border-color 0.15s ease;
}

.product-radio--checked {
  border-color: #5e72e4;
}

.product-radio__dot {
  width: 10px;
  height: 10px;
  border-radius: 50%;
  background-color: #5e72e4;
}

.product-option__label {
  font-size: 15px;
  font-weight: 700;
  color: #344767;
  white-space: nowrap;
}

.product-option__desc {
  font-size: 13px;
  font-weight: 400;
  color: #6c757d;
  white-space: nowrap;
}

@media (max-width: 576px) {
  .product-options {
    flex-direction: column;
  }

  .product-option:not(:last-child) {
    border-right: none;
    border-bottom: 1.5px solid #e2e8ee;
  }

  .product-option__label,
  .product-option__desc {
    white-space: normal;
  }
}
</style>
